import { CostCentreCodesService } from '@/modules/global-settings/general-ledger/services/cost-centre-codes.service';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';

// Create a new instance of the cost centre codes service
const costCentreCodesService = new CostCentreCodesService();

// Cost centre codes module state
export const state = {
  costCentreCodes: [],
  costCentreCode: {}
};

// Cost centre codes module mutations
export const mutations = {
  ...make.mutations(state),

  SET_COST_CENTRE_CODES: (state, payload) => {
    state.costCentreCodes = payload;
  },

  ADD_COST_CENTRE_CODE: (state, payload) => {
    state.costCentreCodes.push(payload);
  },

  UPDATE_COST_CENTRE_CODE: (state, { id, payload }) => {
    const index = state.costCentreCodes.findIndex((el) => el.id == id);
    if (index !== -1) {
      state.costCentreCodes.splice(index, 1, payload);
    }
  },

  UPDATE_DEFAULT_COST_CODES: (state, payload) => {
    state.costCentreCodes.forEach(costCode => {
        if (costCode.useDefaultNumbers) {
          // Find the matching default code
          const defaultCode = payload.find(def => def.name === costCode.name);
          if (defaultCode) {
            // Set the accountNumber on the code
            costCode.accountNumber = defaultCode.accountNumber;
        }
      }
    });
  },

  REMOVE_COST_CENTRE_CODE(state, id) {
    const index = state.costCentreCodes.findIndex((el) => el.id == id);
    state.costCentreCodes.splice(index, 1);
  },
};

// Cost centre codes module actions
export const actions = {
  ...make.actions(state),

  /**
   * Fetch all cost centre codes
   * @param {*} Object - Vuex context 
   * @returns Array - Cost centre codes
   */
  fetchCostCentreCodes: async ({ commit }) => {
    try {
      const { data } = await costCentreCodesService.findAll();
      commit('SET_COST_CENTRE_CODES', data);
      return data;
    } catch (error) {
      Logger.error('fetching cost centre codes');
      throw error;
    }
  },

  /**
   * Create a new cost centre code
   * @param {*} Object - Vuex context
   * @param {*} Object - Cost centre code data
   */
  createCostCentreCode: async ({ commit }, payload) => {
    try {
      const { data } = await costCentreCodesService.create(payload);
      const createdCostCentre = {
        ...payload,
        ...(data && typeof data === 'object' ? data : {})
      };
      commit('ADD_COST_CENTRE_CODE', createdCostCentre);
      return createdCostCentre;
    } catch (error) {
      Logger.error('creating cost centre code');
      throw error;
    }
  },

  /**
   * Update a cost centre code with the given id and data
   * @param {*} Object - Vuex context
   * @param {*} Object - Cost centre code ID & Cost centre code data
   */
  updateCostCentreCode: async ({ commit }, { id, payload }) => {
    try {
      const { data } = await costCentreCodesService.update(id, payload);
      const updatedCostCentre = {
        id,
        ...payload,
        ...(data && typeof data === 'object' ? data : {})
      };
      commit('UPDATE_COST_CENTRE_CODE', {
        id,
        payload: updatedCostCentre
      });
      return updatedCostCentre;
    } catch (error) {
      Logger.error('updating cost centre code');
      throw error;
    }
  },

  /**
   * Delete a cost centre code with the given id
   * @param {*} Object - Vuex context
   * @param {*} Object - Cost centre code ID 
   */
  deleteCostCentreCode: async ({ commit }, id) => {
    try {
      // Call the service to delete the record
      await costCentreCodesService.delete(id);
  
      // Commit the mutation to remove the record from the state
      commit('REMOVE_COST_CENTRE_CODE', id);
  
      return true; // Return success response
    } catch (error) {
      Logger.error('Error deleting cost centre code', error);
      throw error;
    }
  },
};

// Cost centre codes module getters
export const getters = make.getters(state);
